import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Slider } from 'primereact/slider';

/**
 * Escalas de cor oferecidas ao usuário. As chaves batem com HEAT_COLOR_RAMPS em
 * MapboxMap.jsx; a amostra é a mesma sequência de cores, em CSS, para a escolha ser
 * feita olhando o gradiente e não o nome.
 */
export const HEAT_RAMP_OPTIONS = [
    { value: 'hot', label: 'Quente', preview: 'rgba(69,117,180,1), rgba(253,174,97,1), rgba(215,48,39,1)' },
    { value: 'cool', label: 'Frio', preview: 'rgba(0,70,200,1), rgba(100,200,255,1), rgba(200,240,255,1)' },
    { value: 'density', label: 'Densidade', preview: 'rgba(115,0,211,1), rgba(224,64,251,1), rgba(255,255,255,1)' },
    { value: 'profit', label: 'Rentabilidade', preview: 'rgba(0,102,0,1), rgba(136,204,0,1), rgba(255,238,0,1)' },
];

const rampaCss = (preview) => ({ background: `linear-gradient(to right, ${preview})` });

const Amostra = ({ option }) => (
    <div className="heat-ramp-option">
        <span className="heat-ramp-swatch" style={rampaCss(option.preview)} />
        {option.label}
    </div>
);

/**
 * Personalização do mapa de calor: escala de cores, raio, intensidade e opacidade.
 *
 * Usado em dois lugares com o mesmo estado: no modal de criação e na aba Ferramentas
 * enquanto o calor está ativo (onde o ajuste é ao vivo, com o mapa à vista). Por isso
 * é controlado — quem decide o que fazer com a mudança é o call site.
 *
 * @param {{style: {ramp: string, radius: number, intensity: number, opacity: number},
 *          onChange: (patch: object) => void, compact?: boolean}} props
 */
export default function HeatMapControls({ style, onChange, compact = false }) {
    /*
     * `escala` converte entre o valor real e o do slider. O Slider do PrimeReact 6 aplica
     * `Math.floor` no valor que emite (components/slider/Slider.js), então ele só entrega
     * inteiros: um passo de 0,05 na opacidade chegaria aqui como 0 ou 1. min/max/step
     * abaixo estão no espaço do slider (inteiros) e a divisão devolve o valor real —
     * mesmo contorno já usado em ShapeAttributesEditor.
     */
    const campos = [
        {
            key: 'radius',
            label: 'Raio',
            escala: 1,
            min: 5,
            max: 60,
            step: 1,
            valor: `${style.radius} px`,
            dica: 'Alcance de cada ponto. Maior espalha o calor; menor destaca concentrações pontuais.',
        },
        {
            key: 'intensity',
            label: 'Intensidade',
            escala: 10,
            min: 2,
            max: 50,
            step: 1,
            valor: style.intensity.toFixed(1).replace('.', ','),
            dica: 'Quanto cada ponto contribui. Maior satura mais rápido as áreas com muitos empreendimentos.',
        },
        {
            key: 'opacity',
            label: 'Opacidade',
            escala: 100,
            min: 10,
            max: 100,
            step: 5,
            valor: `${Math.round(style.opacity * 100)}%`,
            dica: 'Transparência da camada sobre o mapa base.',
        },
    ];

    return (
        <div className={`heat-controls${compact ? ' heat-controls-compact' : ''}`}>
            <div className="form-group">
                <label className="d-block">Escala de cores</label>
                <Dropdown
                    value={style.ramp}
                    onChange={(e) => onChange({ ramp: e.value })}
                    options={HEAT_RAMP_OPTIONS}
                    optionLabel="label"
                    appendTo={document.body}
                    className="w-100"
                    valueTemplate={(option) => (option ? <Amostra option={option} /> : 'Selecione...')}
                    itemTemplate={(option) => <Amostra option={option} />}
                />
            </div>

            {campos.map(({ key, label, escala, min, max, step, valor, dica }) => (
                <div className="form-group" key={key} title={dica}>
                    <label className="d-flex justify-content-between mb-1">
                        <span>{label}</span>
                        <span className="heat-controls-valor">{valor}</span>
                    </label>
                    <Slider
                        value={Math.round(style[key] * escala)}
                        min={min}
                        max={max}
                        step={step}
                        onChange={(e) => onChange({ [key]: e.value / escala })}
                    />
                </div>
            ))}
        </div>
    );
}
